import { useState, useEffect, useMemo } from "react";
import { DEFAULT_LIMIT_ITEMS } from "constants/data";
import { MstItemDetailTireSelectRequest, Tire } from "../types/tire";
import { fetchTires, updateTire } from "../api/tireApi";

/**
 * Custom hook to fetch and manage a paginated list of companies.
 * This hook uses a placeholder for the actual API call. Replace the commented-out fetchClients function with the actual API call to fetch companies.
 */
const useTireList = () => {
  const [params, setParams] = useState<MstItemDetailTireSelectRequest>({
    item_cd: "",
    brand_name: "",
    maker_name: "",
    tire_size: "",
    limit: DEFAULT_LIMIT_ITEMS,
    offset: 1,
  });
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [tires, setTires] = useState<Tire[]>([]);

  const totalPage = useMemo(() => Math.ceil(total / DEFAULT_LIMIT_ITEMS), [total]);

  /**
   * @param body CompanyQuery
   */
  const getTires = async (body?: MstItemDetailTireSelectRequest) => {
    try {
      const data = await fetchTires(body);
      if (body && body?.offset > 1) {
        setTires((prev) => [...prev, ...data.list.mst_item_detail_tire]);
      } else {
        setTires(data.list.mst_item_detail_tire);
      }
      setTotal(data.total);
    } catch (error) {
      // do nothing
    }
    setLoading(false);
  };

  useEffect(() => {
    getTires();
  }, []);

  const handleSearch = () => {
    getTires({
      ...params,
      offset: 1,
    });
  };

  const onLoadMore = async () => {
    if (page + 1 > totalPage) return;
    setLoading(true);
    setPage((prevPage) => prevPage + 1);
    await getTires({ ...params, offset: page + 1 });
  };

  const handleUpdate = async (tire?: Tire) => {
    try {
      if (tire) {
        await updateTire(tire);
        await getTires();
      }
    } catch (error) {
      console.error("Error updating store:", error);
    }
  };

  return {
    tires,
    params,
    total,
    totalPage,
    page,
    loading,
    handleSearch,
    onLoadMore,
    setParams,
    handleUpdate,
  };
};

export default useTireList;
